import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Scale, Building2, TrendingUp, BarChart3, FileText } from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";

const practiceAreas = [
  {
    title: "Merger & Transaction Antitrust",
    desc: "Strategic antitrust counsel for mergers, acquisitions, and complex transactions—from pre-deal risk assessment through regulatory clearance.",
    icon: Building2,
  },
  {
    title: "Government Investigations",
    desc: "Representation in DOJ, FTC, and State Attorneys General investigations, including civil investigative demands, subpoenas, and enforcement negotiations.",
    icon: Shield,
  },
  {
    title: "Antitrust Litigation",
    desc: "Federal and private antitrust litigation including monopolization claims, competitor disputes, class actions, and distribution restraint cases.",
    icon: Scale,
  },
  {
    title: "Antitrust Counseling",
    desc: "Ongoing advisory on pricing programs, distribution systems, joint ventures, compliance programs, and competitive risk management.",
    icon: FileText,
  },
];

const intelligence = [
  { title: "Competition Policy Intelligence", desc: "Analysis of competition policy developments affecting corporate strategy.", href: "/intelligence", icon: TrendingUp },
  { title: "Merger Risk Radar", desc: "Interactive dashboard tracking merger enforcement trends by industry.", href: "/merger-risk-radar", icon: BarChart3 },
  { title: "Enforcement Risk Index", desc: "Quarterly assessment of the overall antitrust enforcement climate.", href: "/enforcement-index", icon: Shield },
];

const Index = () => (
  <Layout>
    <PageHead
      title="Strategic Antitrust Counsel"
      description="Strategic antitrust counsel for mergers, government investigations, litigation, and competition policy. Batts Legal LLP, Washington, D.C."
      path="/"
      keywords="Alicia Batts, Alicia J. Batts, antitrust lawyer Washington DC, antitrust counsel, competition law, merger antitrust review, FTC attorney, DOJ investigations, Batts Legal LLP"
    />
    {/* Hero */}
    <section className="bg-hero text-primary-foreground section-padding relative overflow-hidden">
      <img src={heroBg} alt="" aria-hidden="true" width={1920} height={1080} className="absolute inset-0 w-full h-full object-cover opacity-40 mix-blend-lighten" />
      <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--primary))] via-[hsl(var(--primary)/0.85)] to-transparent" />
      <div className="editorial-container relative">
        <div className="max-w-3xl">
          <AnimatedSection animation="fade" delay={0}>
            <div className="gold-accent-line mb-8" />
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={100}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold leading-tight mb-6 tracking-tight">
              Strategic Antitrust Counsel for Complex Corporate Matters
            </h1>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={250}>
            <p className="text-lg md:text-xl leading-relaxed opacity-80 mb-10 max-w-2xl">
              Batts Legal LLP advises corporations, investors, and industry leaders on antitrust risk across transactions, investigations, litigation, distribution strategies, and emerging competition policy developments.
            </p>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={400}>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/practice-areas"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity"
              >
                Our Practice <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/intelligence"
                className="inline-flex items-center gap-2 border border-primary-foreground/30 text-primary-foreground px-6 py-3 rounded-sm font-medium text-sm hover:bg-primary-foreground/10 transition-colors"
              >
                Policy Intelligence
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>

    {/* Practice Areas */}
    <section className="section-padding bg-surface-gradient">
      <div className="editorial-container">
        <AnimatedSection>
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Core Practice</p>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">Practice Areas</h2>
            <p className="text-muted-foreground leading-relaxed">Focused antitrust expertise across the full spectrum of competition law challenges.</p>
          </div>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 gap-6">
          {practiceAreas.map((area, i) => (
            <AnimatedSection key={area.title} animation="fade-up" delay={i * 100}>
              <Link
                to="/practice-areas"
                className="group bg-card rounded-md p-8 shadow-card hover:shadow-elevated transition-all border border-border block h-full"
              >
                <area.icon className="w-8 h-8 text-navy-500 mb-4" />
                <h3 className="font-serif text-xl font-semibold text-foreground mb-3 group-hover:text-navy-600 transition-colors">{area.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{area.desc}</p>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* High-Stakes CTA */}
    <AnimatedSection animation="fade">
      <section className="bg-navy-900 text-primary-foreground py-16">
        <div className="editorial-container flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-xl">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold mb-3">Facing a High-Stakes Antitrust Matter?</h2>
            <p className="opacity-70 leading-relaxed">When critical competition issues arise, Batts Legal LLP provides rapid strategic assessment and coordinates with economists and industry experts.</p>
          </div>
          <Link
            to="/high-stakes"
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-sm font-medium text-sm whitespace-nowrap hover:opacity-90 transition-opacity"
          >
            Explore High-Stakes Antitrust Matters <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </AnimatedSection>

    {/* Intelligence Platform */}
    <section className="section-padding">
      <div className="editorial-container">
        <AnimatedSection>
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Intelligence</p>
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mb-4">Competition Policy Intelligence</h2>
            <p className="text-muted-foreground leading-relaxed">Ongoing analysis of competition policy developments for corporate leaders, deal teams, and general counsel.</p>
          </div>
        </AnimatedSection>
        <div className="grid md:grid-cols-3 gap-6">
          {intelligence.map((item, i) => (
            <AnimatedSection key={item.title} animation="fade-up" delay={i * 120}>
              <Link
                to={item.href}
                className="group bg-card rounded-md p-8 shadow-card hover:shadow-elevated transition-all border border-border block h-full"
              >
                <item.icon className="w-8 h-8 text-gold mb-4" />
                <h3 className="font-serif text-lg font-semibold text-foreground mb-2 group-hover:text-navy-600 transition-colors">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-navy-500 group-hover:text-navy-700 transition-colors">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* Target Clients */}
    <section className="section-padding bg-muted/50">
      <div className="editorial-container">
        <AnimatedSection>
          <div className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Who We Serve</p>
            <h2 className="text-3xl font-serif font-semibold text-foreground mb-4">Trusted by Corporate Leaders</h2>
          </div>
        </AnimatedSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            "Public & Private Corporations",
            "Private Equity & Investment Funds",
            "Merging Companies",
            "Companies Under Investigation",
            "Antitrust Litigation Defendants",
            "Distribution & Pricing Enterprises",
            "Trade Associations",
            "General Counsel & Boards",
          ].map((client, i) => (
            <AnimatedSection key={client} animation="scale" delay={i * 60}>
              <div className="bg-card rounded-md p-5 text-center shadow-card border border-border h-full">
                <p className="text-sm font-medium text-foreground">{client}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* Contact CTA */}
    <section className="section-padding">
      <div className="editorial-container">
        <AnimatedSection animation="scale">
          <div className="bg-card rounded-lg p-12 md:p-16 shadow-elevated border border-border text-center max-w-3xl mx-auto">
            <div className="gold-accent-line mx-auto mb-6" />
            <h2 className="font-serif text-3xl font-semibold text-foreground mb-4">Engage Batts Legal LLP</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              For strategic antitrust counsel on transactions, investigations, litigation, or competition policy matters, contact our team.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity"
            >
              Contact Us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>
  </Layout>
);

export default Index;
